import React, { useContext, useEffect, useState } from "react"
import "twin.macro"
import { useInterval } from "../../../utils/hooks/useInterval"
import { Question, QuestionState, useGetNewestOpenQuestionLazyQuery } from "../../../graphql/generated/graphql"
import { QuestionProps } from "../../../pages/q/[id]"
import Loading from "../../../components/loading"
import { Container } from "../../../components/page/gridContainer"
import { LottieAnimation } from "../../lottieAnimation"
import animationData from "../../../assets/question/available_soon.json"
import { isMobile } from "react-device-detect"
import { Events, registerEvent } from "../../../analytics"
import QAText from "../qaText"
import { DateTime } from "luxon"
import { AuthContext } from "../../../utils/context"
import { NextOpenQuestion } from "../nextQuestion"
import Link from "next/link"
import { Routes } from "../../../routes"
import { useTranslation } from "next-i18next"
import { useEffectAsync } from "../../../utils/hooks/useEffectAsync"

export const QuestionPublishing = (props: QuestionProps) => {
  const { refetchQuestion, question } = props
  const { createdAt, Fan, Artist, body } = question
  const { userId } = useContext(AuthContext)
  const [newestOpenQuestion, setNewestOpenQuestion] = useState<Question | undefined | null>()
  const { id, isTakingQuestions } = Artist
  const isArtist = userId === id

  useInterval(async () => {
    switch (question.state) {
      case QuestionState.AnswerPublishing:
        await refetchQuestion()
        break
      default:
        return <Loading />
    }
  }, 3000)

  useEffect(() => {
    registerEvent(Events.VisitedAnswerProcessing)
  }, [])

  const [getArtistNextOpenQuestionNav] = useGetNewestOpenQuestionLazyQuery()
  useEffectAsync(async () => {
    if (isArtist && isTakingQuestions) {
      const res = await getArtistNextOpenQuestionNav()
      if (res && res.data && res.data.NewestOpenQuestion) {
        setNewestOpenQuestion(res.data.NewestOpenQuestion as Question)
      }
    }
  }, [])

  const { t } = useTranslation("question")
  const formatDate = (time: string) => DateTime.fromISO(time).toFormat("MMM dd")

  const questionTextProps = {
    date: formatDate(createdAt),
    username: Fan.username,
    name: Fan.name ? Fan.name : Fan.username,
    isAnswer: false,
    body: body,
  }

  return (
    <section tw="py-8 mb-12 h-full">
      <Container>
        <div tw="grid grid-cols-4 tablet:grid-cols-12 gap-4 desktop:gap-6 gap-y-4">
          <div tw="col-span-4 space-y-8 tablet:(col-span-8 col-start-3) tablet-lg:(col-span-8 col-start-3)">
            <h1 tw="py-2 text-xl font-bold text-center">
              {t("answer_from")}
              <Link href={{ pathname: Routes.Profile, query: { username: Artist.username } }} passHref>
                <a data-testid="artist-profile-link" tw="block tablet:(inline)">
                  {Artist.name}
                </a>
              </Link>
            </h1>
          </div>
          {newestOpenQuestion && (
            <div tw="col-span-4 order-2 pb-1 tablet:col-span-12 tablet-lg:(col-span-8 col-start-3 order-1)">
              <NextOpenQuestion {...{ question: newestOpenQuestion }} />
            </div>
          )}
          <div tw="col-span-4 order-1 tablet:(col-span-6 order-2) tablet-lg:(col-span-4 col-start-3 order-2)">
            <div tw="flex flex-col space-y-4">
              <LottieAnimation
                data-chromatic="ignore"
                width={isMobile ? "234px" : "300px"}
                height="auto"
                animationData={animationData}
              />
            </div>
          </div>
          <div tw="col-span-4 order-3 tablet:(col-span-6 order-3) tablet-lg:(col-span-4 order-3)">
            <QAText {...questionTextProps} />
          </div>
        </div>
      </Container>
    </section>
  )
}
